import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';
import { useDarkMode } from '../../hooks/useDarkMode';

const DashboardHeader = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [isDark, setIsDark] = useDarkMode(); 
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  const [userData, setUserData] = useState({
    id: null,
    nomeCompleto: '...',
    ruolo: t('header.loading') 
  });

  const [unreadDocs, setUnreadDocs] = useState([]);

  // Pomoćna funkcija za formatiranje datuma u dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleLogoClick = () => {
    if (userData.ruolo === 'User') {
      navigate('/home-dashboard-dipendente');
    } else if (userData.ruolo === 'Owner') {
      navigate('/home-dashboard-owner');
    } else {
      navigate('/admin-menu-page'); 
    }
  };

  const changeTheme = (themeName) => {
    const body = document.body;
    const themes = ['theme-green', 'theme-black', 'theme-purple', 'theme-orange'];
    themes.forEach(theme => body.classList.remove(theme));
    
    if (themeName !== 'default') {
      body.classList.add(`theme-${themeName}`);
      localStorage.setItem('selected-theme', `theme-${themeName}`);
    } else {
      localStorage.setItem('selected-theme', 'default');
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('selected-theme');
    if (savedTheme && savedTheme !== 'default') {
      document.body.classList.add(savedTheme);
    }
  }, []);

  const languages = [
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'bs', name: 'Bosanski', flag: '🇧🇦' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  const langMenuRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) setShowLangMenu(false);
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) setShowSettingsMenu(false);
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setShowUserMenu(false);
      if (notificationRef.current && !notificationRef.current.contains(event.target)) setShowNotifications(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Funkcija za povlačenje podataka - odvojena da je Real-time može lakše zvati
  const fetchData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profilo, error: profError } = await supabase
        .from('profili')
        .select('id, nome, cognome, ruolo')
        .eq('id', user.id)
        .single();

      if (profError) throw profError;

      setUserData({
        id: profilo.id,
        nomeCompleto: `${profilo.nome} ${profilo.cognome}`,
        ruolo: profilo.ruolo || 'User'
      });

      // Povlačenje isključivo nepročitanih dokumenata
      const { data: docs, error: docError } = await supabase
        .from('documenti')
        .select('*')
        .eq('id_utente', profilo.id)
        .eq('procitano', false)
        .order('created_at', { ascending: false });

      if (!docError) {
        setUnreadDocs(docs || []);
      }

    } catch (err) {
      console.error("Error fetching header data:", err.message);
    }
  };

  useEffect(() => {
    fetchData();

    // REAL-TIME: Slušamo sve promjene u tabeli 'documenti'
    // Čim ti (radnik) uradiš UPDATE na procitano=true, ovo će okinuti fetchData()
    const channel = supabase
      .channel('header-notifications-live')
      .on(
        'postgres_changes', 
        { 
          event: '*', // Slušaj INSERT (novi fajl) i UPDATE (kad postane pročitan)
          schema: 'public', 
          table: 'documenti' 
        }, 
        (payload) => {
          console.log('Promjena u bazi detektovana:', payload);
          fetchData(); // Ponovo povuci podatke da ažuriraš broj u zvoncu
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [i18n.language]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (err) {
      console.error("Logout error:", err.message);
    }
  };

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <header className="header" role="banner">
      <div className="header-container relative">
        <div className="header-logo cursor-pointer" onClick={handleLogoClick}>
          <div className="header-logo-icon">
            <div className="relative flex items-center justify-center">
              <Icon name="Shield" size={24} color="var(--color-primary)" strokeWidth={2} />
              <span className="absolute inset-0 flex items-center justify-center font-black text-primary select-none" style={{ fontSize: '11px', transform: 'translateY(-5%)', lineHeight: 1 }}>O</span>
            </div>
          </div>
          <span className="header-logo-text">OrizonDesk</span>
        </div>

        <nav className="header-actions flex items-center gap-2">
          {/* Language Menu */}
          <div className="relative" ref={langMenuRef}>
            <button onClick={() => setShowLangMenu(!showLangMenu)} className="header-action-button flex items-center gap-2 px-2">
              <span className="text-xl">{currentLang.flag}</span>
              <Icon name="ChevronDown" size={14} />
            </button>
            {showLangMenu && (
              <div className="absolute top-12 right-0 bg-card rounded-lg shadow-xl border border-border z-[1000] min-w-[160px] py-2">
                {languages.map((lang) => (
                  <button key={lang.code} onClick={() => { i18n.changeLanguage(lang.code); setShowLangMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-muted ${i18n.language === lang.code ? 'bg-primary/5 text-primary font-bold' : ''}`}>
                    <span className="text-xl">{lang.flag}</span>
                    <span className="text-sm">{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="h-6 w-[1px] bg-border mx-1"></div>

          {/* ZVONCE - NOTIFIKACIJE */}
          <div className="relative" ref={notificationRef}>
            <button className={`header-action-button relative ${showNotifications ? 'bg-primary/10 text-primary' : ''}`} onClick={() => setShowNotifications(!showNotifications)}>
              <Icon name="Bell" size={20} />
              {unreadDocs.length > 0 && <span className="absolute top-2.5 right-2.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-background animate-pulse" />}
            </button>

            {showNotifications && (
              <div className="absolute top-12 right-0 bg-card rounded-xl shadow-2xl border border-border z-[1000] min-w-[300px] overflow-hidden">
                <div className="p-4 border-b border-border bg-muted/20">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-foreground flex items-center justify-between">
                    {t('notifikacije.title_not')}
                    {unreadDocs.length > 0 && <span className="bg-red-500 text-white text-[9px] px-2 py-0.5 rounded-full animate-bounce">{unreadDocs.length}</span>}
                  </h3>
                </div>

                <div className="max-h-[350px] overflow-y-auto">
                  {unreadDocs.length > 0 ? (
                    unreadDocs.map((doc) => (
                      <button key={doc.id} onClick={() => { setShowNotifications(false); navigate('/my-documents'); }} className="w-full text-left p-4 hover:bg-primary/5 border-b border-border last:border-0 transition-all flex gap-3 group">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors shrink-0">
                          <Icon name="FileText" size={20} />
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <p className="text-[10px] font-black uppercase text-primary mb-1">
                            {t(`myDocuments.types.${doc.tipologia.charAt(0).toLowerCase() + doc.tipologia.slice(1).replace(' ', '')}`, { defaultValue: doc.tipologia })}
                          </p>
                          <p className="text-xs font-bold truncate text-foreground leading-tight">{doc.nome_file}</p>
                          <p className="text-[10px] text-muted-foreground mt-1 font-medium italic">
                            {formatDate(doc.created_at)}
                          </p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-10 text-center">
                      <Icon name="CheckCircle2" size={32} className="mx-auto text-emerald-500/30 mb-3" />
                      <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">{t('notifikacije.empty')}</p>
                    </div>
                  )}
                </div>

                {unreadDocs.length > 0 && (
                  <div className="p-2 bg-muted/30 border-t border-border">
                    <button onClick={() => { setShowNotifications(false); navigate('/my-documents'); }} className="w-full text-center py-2 text-[10px] font-black uppercase text-primary hover:bg-primary/5 rounded-lg transition-colors">
                      {t('notifikacije.view_all')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Settings Menu */}
          <div className="relative" ref={settingsMenuRef}>
            <button onClick={() => setShowSettingsMenu(!showSettingsMenu)} className={`header-action-button ${showSettingsMenu ? 'bg-primary/10 text-primary' : ''}`}>
              <Icon name="Settings" size={20} />
            </button>
            {showSettingsMenu && (
              <div className="absolute top-12 right-0 bg-card rounded-xl shadow-xl border border-border z-[1000] min-w-[220px] overflow-hidden">
                <div className="p-4">
                  <p className="text-[10px] font-black uppercase text-muted-foreground mb-3 tracking-widest flex items-center gap-2">
                    <Icon name="Palette" size={12} /> {t('settings.theme_title')}
                  </p>
                  <div className="grid grid-cols-5 gap-2 mb-4">
                    <button onClick={() => changeTheme('default')} className="w-6 h-6 rounded-full bg-[#1E3A5F] border border-border hover:scale-125 transition-transform" />
                    <button onClick={() => changeTheme('green')} className="w-6 h-6 rounded-full bg-[#059669] border border-border hover:scale-125 transition-transform" />
                    <button onClick={() => changeTheme('black')} className="w-6 h-6 rounded-full bg-[#111827] border border-border hover:scale-125 transition-transform" />
                    <button onClick={() => changeTheme('purple')} className="w-6 h-6 rounded-full bg-[#7C3AED] border border-border hover:scale-125 transition-transform" />
                    <button onClick={() => changeTheme('orange')} className="w-6 h-6 rounded-full bg-[#EA580C] border border-border hover:scale-125 transition-transform" />
                  </div>
                  <div className="h-[1px] bg-border w-full my-4" />
                  <button onClick={() => setIsDark(!isDark)} className="w-full flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors border border-border">
                    <div className="flex items-center gap-2">
                      <Icon name={isDark ? "Sun" : "Moon"} size={16} className={isDark ? "text-yellow-500" : "text-primary"} />
                      <span className="text-xs font-bold uppercase tracking-wider">{isDark ? t('settings.light_mode') : t('settings.dark_mode')}</span>
                    </div>
                    <div className={`w-8 h-4 rounded-full relative transition-colors ${isDark ? 'bg-primary' : 'bg-gray-300'}`}>
                      <div className={`absolute top-1 bg-white w-2 h-2 rounded-full transition-transform ${isDark ? 'translate-x-5' : 'translate-x-1'}`} />
                    </div>
                  </button>
                </div>
                <div className="p-2 bg-muted/30 border-t border-border">
                  <button onClick={() => { setShowSettingsMenu(false); navigate('/settings'); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted text-foreground transition-colors">
                    <Icon name="Settings" size={16} />
                    <span className="text-sm font-medium">{t('header.settings')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
         
          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button onClick={() => setShowUserMenu(!showUserMenu)} className={`header-action-button ${showUserMenu ? 'bg-primary/10 text-primary' : ''}`}>
              <Icon name="User" size={20} />
            </button>
            {showUserMenu && (
              <div className="absolute top-12 right-0 bg-card rounded-lg shadow-xl border border-border z-[999] min-w-[220px]">
                <div className="p-4 border-b border-border">
                  <p className="text-sm font-bold text-foreground">{userData.nomeCompleto}</p>
                  <p className="text-[10px] uppercase font-bold tracking-wider text-primary mt-1 bg-primary/10 px-2 py-0.5 rounded-full inline-block">{userData.ruolo}</p>
                </div>
                <div className="p-2">
                  <button onClick={() => { setShowUserMenu(false); navigate('/mio-profilo'); }} className="w-full flex items-center gap-3 px-4 py-2 rounded-md hover:bg-muted text-foreground transition-colors">
                    <Icon name="User" size={16} />
                    <span className="text-sm">{t('header.my_profile')}</span>
                  </button>
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2 rounded-md hover:bg-red-50 text-red-600 font-medium transition-colors">
                    <Icon name="LogOut" size={16} />
                    <span className="text-sm">{t('header.logout')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default DashboardHeader;